<template>
  <div class="listbox-item" ref="listbox-item" :class="listboxItemClass" role="option" :aria-selected="isSelected">
    <slot name="prefix">
      <!-- Sized against the row's 14px text: the icon default (24px) reads as a picture, not a marker -->
      <Icon v-if="icon" :name="icon" size="small" class="listbox-item-icon" />
    </slot>

    <View class="listbox-item-text" direction="column" justify="center" gap="xs" :grow="1">
      <Text class="listbox-item-title" variant="body-medium" v-if="title">
        {{ title }}
      </Text>

      <Text class="listbox-item-subtitle" variant="body-small" v-if="subtitle">
        {{ subtitle }}
      </Text>
    </View>

    <LoadingIndicator v-if="isLoading" />

    <!-- After the text: a count, a unit -->
    <span v-if="$slots.suffix" class="listbox-item-suffix">
      <slot name="suffix" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import Icon from '../icon.vue';
import LoadingIndicator from '../loading-indicator.vue';
import Text from '../text.vue';
import View from '../view/view.vue';
import { ListboxItemProps } from './listbox-item-props.interface';

const props = withDefaults(defineProps<ListboxItemProps>(), {
  variant: 'normal',
  isLoading: true,
});

const listboxItemRef = useTemplateRef<HTMLDivElement>('listbox-item');

const listboxItemClass = computed(() => [
  {
    active: props.isActive,
    selected: props.isSelected,
    loading: props.isLoading,
  },
  props.variant,
]);

defineExpose({ listboxItemRef });
</script>

<style scoped lang="scss">
.listbox-item,
.listbox-item-text {
  height: 3rem;
}

.listbox-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  padding-inline: var(--t-spacing-md);

  &:hover,
  &.active {
    background-color: var(--t-color-neutral-200);
    cursor: pointer;
  }

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: -2px;
  }

  // At either end of a rounded list the row takes the list's corners, so a focus ring there follows
  // the curve instead of being cut square by it
  &:first-child {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  &:last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }

  .listbox-item-icon,
  .listbox-item-suffix {
    flex-shrink: 0;
  }

  // The chosen row is told by its title alone, in bold: no mark to size or place
  &.selected .listbox-item-title {
    font-weight: var(--t-font-weight-semi-bold);
  }

  .listbox-item-suffix {
    display: inline-flex;
    align-items: center;
  }

  // A long entry is cut with an ellipsis rather than wrapping past the row's fixed height
  .listbox-item-text {
    min-width: 0;
  }

  .listbox-item-title,
  .listbox-item-subtitle {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .listbox-item-subtitle {
    color: var(--t-text-color-secondary);
  }

  &.loading {
    .listbox-item-text,
    :slotted([name='prefix']),
    .listbox-item-icon {
      opacity: 0.5;
    }
  }

  &.dangerous {
    color: var(--t-color-red-400);

    .listbox-item-title,
    .listbox-item-subtitle {
      color: var(--t-color-red-400);
    }
  }

  &.info {
    color: var(--t-color-blue-500);

    .listbox-item-title,
    .listbox-item-subtitle {
      color: var(--t-color-blue-500);
    }
  }
}
</style>
